// Problems API client aligned to docs/api.md and roadmap.
// Supports mock mode for local testing when REACT_APP_USE_MOCK_API=true.

let API_BASE = process.env.REACT_APP_API_BASE || "/api";
if (
  typeof window !== "undefined" &&
  window.location?.protocol === "https:" &&
  API_BASE.startsWith("http://")
) {
  // Avoid Mixed Content on HTTPS deployments when a dev HTTP base URL is accidentally baked in.
  console.warn(
    `[API] Overriding insecure API base (${API_BASE}) to /api for HTTPS page.`
  );
  API_BASE = "/api";
}
const USE_MOCK = process.env.REACT_APP_USE_MOCK_API === "true";
// Optional dev fallback; default off unless explicitly enabled
const DEV_FALLBACK_TO_MOCK = process.env.REACT_APP_DEV_FALLBACK === "true";
const API_LOGGING = process.env.REACT_APP_API_LOGGING !== "false";

const logApi = (message, payload) => {
  if (!API_LOGGING) return;
  if (payload) {
    console.log(`[API] ${message}`, payload);
    return;
  }
  console.log(`[API] ${message}`);
};

const resolveRequestUrl = (path) => {
  const url = `${API_BASE}${path}`;
  if (typeof window === "undefined" || !window.location?.href) return url;
  try {
    return new URL(url, window.location.href).toString();
  } catch {
    return url;
  }
};

const toNetworkError = (error, path) => {
  const url = resolveRequestUrl(path);
  const devHint =
    process.env.NODE_ENV === "development"
      ? "Is the API server running? Try `npm run dev` (or `npm run server` in another terminal)."
      : "Please check your network and try again.";
  const originalMessage =
    error instanceof Error && error.message ? error.message : String(error || "");
  const originalSuffix =
    originalMessage && originalMessage !== "Failed to fetch"
      ? ` (Original error: ${originalMessage})`
      : "";

  const networkError = new Error(`Failed to reach API: ${url}. ${devHint}${originalSuffix}`);
  try {
    networkError.cause = error;
  } catch {
    // ignore
  }
  return networkError;
};

const summarizeBody = (body) => {
  if (!body) return null;
  if (typeof body === "string") {
    return {
      type: "string",
      length: body.length,
      preview: body.slice(0, 180),
    };
  }
  return { type: typeof body };
};

const summarizeFormData = (formData) => {
  if (!formData || typeof formData.entries !== "function") {
    return { entries: [] };
  }
  const entries = [];
  try {
    for (const [key, value] of formData.entries()) {
      if (typeof File !== "undefined" && value instanceof File) {
        entries.push({
          key,
          fileName: value.name,
          fileSize: value.size,
          fileType: value.type,
        });
      } else {
        entries.push({ key, value: String(value).slice(0, 180) });
      }
    }
  } catch (error) {
    return { entries: [], error: error.message };
  }
  return { entries };
};

const doFetch = async (path, options = {}) => {
  logApi(`request ${options.method || "GET"} ${API_BASE}${path}`, {
    body: summarizeBody(options.body),
  });
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
      ...options,
    });
  } catch (error) {
    logApi(`error ${options.method || "GET"} ${API_BASE}${path}`, {
      message: error instanceof Error ? error.message : String(error || ""),
    });
    throw toNetworkError(error, path);
  }
  logApi(`response ${options.method || "GET"} ${API_BASE}${path}`, {
    status: res.status,
    ok: res.ok,
  });
  if (!res.ok) {
    const text = await res.text();
    logApi(`error ${options.method || "GET"} ${API_BASE}${path}`, {
      status: res.status,
      body: text.slice(0, 500),
    });
    throw new Error(text || `Request failed: ${res.status}`);
  }
  if (res.status === 204) return null;
  const data = await res.json();
  logApi(`payload ${options.method || "GET"} ${API_BASE}${path}`, data);
  return data;
};

const doFormPost = async (path, formData) => {
  logApi(`request POST ${API_BASE}${path}`, summarizeFormData(formData));
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      body: formData,
    });
  } catch (error) {
    logApi(`error POST ${API_BASE}${path}`, {
      message: error instanceof Error ? error.message : String(error || ""),
    });
    throw toNetworkError(error, path);
  }
  logApi(`response POST ${API_BASE}${path}`, { status: res.status, ok: res.ok });
  if (!res.ok) {
    const text = await res.text();
    logApi(`error POST ${API_BASE}${path}`, {
      status: res.status,
      body: text.slice(0, 500),
    });
    throw new Error(text || `Request failed: ${res.status}`);
  }
  const data = await res.json();
  logApi(`payload POST ${API_BASE}${path}`, data);
  return data;
};

const mockId = () => Math.random().toString(36).slice(2, 10);

const mockFromText = (payload) => {
  const id = mockId();
  return {
    problemId: id,
    problem: {
      id,
      rawProblem: payload?.text || "",
      latex: "\\int x e^x \\, dx",
      metadata: {
        problemType: "integration_by_parts",
        approach: "ibp_u_polynomial_v_exponential",
        difficultyScore: 2,
        techniques: ["integration", "ibp"],
        tags: ["product_rule_pattern"],
        source: payload?.source || "manual",
      },
      hintLevel1: {
        level: 1,
        text: "You have a product of x and e^x; try integration by parts with u = x.",
      },
    },
  };
};

const mockFromImage = (payload) => {
  const id = mockId();
  return {
    problemId: id,
    problem: {
      id,
      rawProblem: "Mock image problem",
      latex: "\\int x e^x \\, dx",
      imageUrl: payload?.imageUrl || "https://example.com/mock.png",
      metadata: {
        problemType: "integration_by_parts",
        approach: "ibp_u_polynomial_v_exponential",
        difficultyScore: 2,
        techniques: ["integration", "ibp"],
        tags: ["product_rule_pattern"],
        source: payload?.source || "screenshot",
      },
      canonicalSolution: {
        finalAnswer: "x e^x - e^x + C",
        steps: [
          { id: 1, description: "Let u = x, dv = e^x dx", mathLatex: "u=x, dv=e^x dx" },
        ],
        source: "wolfram-alpha",
      },
      hintLevel1: { level: 1, text: "Try integration by parts." },
      oneNoteUrl: "https://onenote.example.com/mock",
    },
  };
};

const mockDesmosSuggest = (payload) => ({
  problemId: payload?.problemId || mockId(),
  expressions: [
    { id: "expr-0", label: "Mock curve", latex: "y=x^2" },
    { id: "expr-1", label: "Mock line", latex: "y=4" },
  ],
});

export const fromText = async (payload) => {
  if (USE_MOCK || DEV_FALLBACK_TO_MOCK) {
    return mockFromText(payload);
  }
  try {
    return await doFetch("/problems/from-text", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  } catch (error) {
    throw error;
  }
};

export const fromImage = async (payload) => {
  if (USE_MOCK || DEV_FALLBACK_TO_MOCK) {
    return mockFromImage(payload);
  }
  try {
    if (payload?.file) {
      const fd = new FormData();
      fd.append("file", payload.file);
      if (payload?.source) fd.append("source", payload.source);
      if (payload?.mode) fd.append("mode", payload.mode);
      return await doFormPost("/problems/from-image", fd);
    }
    return await doFetch("/problems/from-image", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  } catch (error) {
    throw error;
  }
};

export const enrichProblem = async (payload) => {
  if (USE_MOCK || DEV_FALLBACK_TO_MOCK) {
    return {
      metadata: {},
      canonicalSolution: null,
      hintLevel1: null,
    };
  }
  try {
    return await doFetch("/problems/enrich", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  } catch (error) {
    throw error;
  }
};

export const checkAnswer = async (payload) => {
  if (USE_MOCK || DEV_FALLBACK_TO_MOCK) {
    return {
      correct: true,
      confidence: 0.75,
      feedback: "Mock check: looks correct.",
    };
  }
  try {
    return await doFetch("/answers/check", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  } catch (error) {
    throw error;
  }
};

export const getProblem = async (problemId) => {
  if (USE_MOCK || DEV_FALLBACK_TO_MOCK) {
    return {
      id: problemId,
      rawProblem: "Mock problem",
      latex: "\\int x e^x \\, dx",
      problemText: "Integrate x e^x dx",
      metadata: {
        problemType: "integration_by_parts",
        approach: "ibp_u_polynomial_v_exponential",
        difficultyScore: 2,
        techniques: ["integration", "ibp"],
        tags: ["product_rule_pattern"],
      },
      canonicalSolution: {
        finalAnswer: "x e^x - e^x + C",
        steps: [{ id: 1, description: "IBP with u=x, dv=e^x dx" }],
      },
      hints: [{ level: 1, text: "Try integration by parts." }],
      oneNoteUrl: "https://onenote.example.com/mock",
      hintTimeline: [],
      mistakeTags: [],
      finalReflection: "",
      attachments: [],
    };
  }
  try {
    return await doFetch(`/problems/${problemId}`);
  } catch (error) {
    throw error;
  }
};

export const listHints = async (problemId) => {
  if (USE_MOCK || DEV_FALLBACK_TO_MOCK) {
    return {
      problemId,
      hints: [{ level: 1, text: "Try integration by parts.", resources: [] }],
    };
  }
  try {
    return await doFetch(`/problems/${problemId}/hints`);
  } catch (error) {
    throw error;
  }
};

export const generateHints = async (problemId, payload) => {
  if (USE_MOCK || DEV_FALLBACK_TO_MOCK) {
    const levels = Array.isArray(payload) ? payload : payload?.levels;
    return {
      problemId,
      generatedLevels: levels || [1],
      hints: [
        { level: 1, text: "Classify the problem type before choosing a method.", resources: [] },
        { level: 2, text: "Consider which integration strategy fits the structure.", resources: [] },
        { level: 3, text: "Identify the key rewrite or setup that unlocks progress.", resources: [] },
        { level: 4, text: "Outline the full solution path without computing.", resources: [] },
        { level: 5, text: "Full explanation goes here (mock).", resources: [] },
      ],
    };
  }
  try {
    const body = Array.isArray(payload) ? { levels: payload } : payload || {};
    return await doFetch(`/problems/${problemId}/hints/generate`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  } catch (error) {
    throw error;
  }
};

export const adaptHintForLevel = async (problemId, payload) => {
  if (USE_MOCK || DEV_FALLBACK_TO_MOCK) {
    return {
      problemId,
      hint: {
        level: payload?.level || 1,
        text: "Adapted hint (mock): reflect on the student's update before proceeding.",
      },
    };
  }
  try {
    return await doFetch(`/problems/${problemId}/hints/adapt`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    });
  } catch (error) {
    throw error;
  }
};

export const suggestDesmosExpressions = async (problemId, payload) => {
  const path = `/problems/${problemId}/desmos/suggest`;
  if (USE_MOCK || DEV_FALLBACK_TO_MOCK) {
    return mockDesmosSuggest({ ...payload, problemId });
  }
  return doFetch(path, {
    method: "POST",
    body: JSON.stringify(payload || {}),
  });
};

export const generateHintsStream = async (
  problemId,
  payload,
  { onEvent, signal, streamDeltas = false } = {}
) => {
  if (USE_MOCK || DEV_FALLBACK_TO_MOCK) {
    const result = await generateHints(problemId, payload);
    onEvent?.({ type: "done", problemId, hints: result.hints });
    return result;
  }

  const body = {
    ...(Array.isArray(payload) ? { levels: payload } : payload || {}),
    streamDeltas,
  };

  logApi(`request POST ${API_BASE}/problems/${problemId}/hints/generate-stream`, {
    body: summarizeBody(JSON.stringify(body)),
  });

  const res = await fetch(`${API_BASE}/problems/${problemId}/hints/generate-stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });

  logApi(
    `response POST ${API_BASE}/problems/${problemId}/hints/generate-stream`,
    { status: res.status, ok: res.ok }
  );

  if (!res.ok) {
    const text = await res.text();
    logApi(`error POST ${API_BASE}/problems/${problemId}/hints/generate-stream`, {
      status: res.status,
      body: text.slice(0, 500),
    });
    throw new Error(text || `Request failed: ${res.status}`);
  }

  if (!res.body) {
    throw new Error("Streaming response body is not available in this browser.");
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  const hints = [];

  const handleEvent = (event) => {
    if (!event) return;
    onEvent?.(event);
    if (API_LOGGING && event.type && event.type !== "delta") {
      console.log("[API] stream event", event);
    }
    if (event.type === "hint" && event.hint) {
      hints.push(event.hint);
    }
  };

  while (true) {
    // eslint-disable-next-line no-await-in-loop
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      try {
        const event = JSON.parse(trimmed);
        handleEvent(event);
        if (event.type === "error") {
          throw new Error(event.error || "Streaming error");
        }
        if (event.type === "done") {
          return {
            problemId: event.problemId || problemId,
            hints: event.hints || hints,
          };
        }
      } catch (error) {
        console.warn("Failed to parse stream event", error, trimmed.slice(0, 200));
      }
    }
  }

  return { problemId, hints };
};

export const addAttachment = async (problemId, attachment) => {
  if (USE_MOCK) return null;
  return doFetch(`/problems/${problemId}/attachments`, {
    method: "POST",
    body: JSON.stringify(attachment),
  });
};

export const refreshOneNote = async (problemId) => {
  if (USE_MOCK) return null;
  return doFetch(`/problems/${problemId}/refresh-onenote`, {
    method: "POST",
  });
};

export const logHintUsage = async (problemId, event) => {
  if (USE_MOCK) return null;
  return doFetch(`/problems/${problemId}/hints/log`, {
    method: "POST",
    body: JSON.stringify(event),
  });
};

export const answerChat = async (sessionId, payload) => {
  if (USE_MOCK || DEV_FALLBACK_TO_MOCK) {
    return {
      sessionId,
      assistantMessage: "I'm a mock assistant. I can't help you yet.",
    };
  }
  try {
    return await doFetch(`/sessions/${sessionId}/chat`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  } catch (error) {
    throw error;
  }
};
